import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import React, {
    forwardRef,
    useContext,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { AppTopbarRef } from "../../types";
import { LayoutContext } from "../../contexts/LayoutContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useAppMenuModel } from "../AppSidebar/useAppMenuModel";
import Auth from "../../utils/Auth";
import { findActiveMenuTrail } from "../../utils/menuNavigation";

const AppTopbar = forwardRef<AppTopbarRef>((props, ref) => {
    const {
        showProfileSidebar: showProfileSidebar,
        showConfigSidebar: showConfigSidebar,
    } = useContext(LayoutContext);
    const { toggleSidebar } = useSidebar();
    const [vinNumbers, setVinNumbers] = useState<string[]>([]);
    const [search, setSearch] = useState<string>("");
    const menubuttonRef = useRef(null);
    const auth = new Auth();
    const navigate = useNavigate();
    const location = useLocation();
    const menuModel = useAppMenuModel();
    const user: any = auth.getUser() || { name: "" };
    const inputSearchRef: any = useRef();
    const vinPanelRef = useRef<OverlayPanel>(null);
    const { t } = useTranslation();

    const activePage = useMemo(
        () => findActiveMenuTrail(menuModel, location.pathname),
        [location.pathname, menuModel]
    );

    const isDashboardHome =
        location.pathname === "/dashboard" || location.pathname === "/";
    const pageTitle = activePage?.label ?? t("dashboard");
    const pageSection = activePage?.section;
    const showTrail = !isDashboardHome && Boolean(pageTitle);
    const profileName = String(user?.name ?? "").trim();

    const onConfigButtonClick = () => {
        showConfigSidebar();
    };

    useImperativeHandle(ref, () => ({
        menubutton: menubuttonRef.current,
    }));

    const onSearchVin = () => {
        if (vinNumbers.length === 0) {
            return;
        }

        const vinString = vinNumbers.join(" ");

        if (location.pathname === "/search/vins") {
            navigate("/temporary", { replace: true });
            setTimeout(() => {
                navigate("/search/vins", {
                    state: { vin_numbers: vinString },
                    replace: true,
                });
                setVinNumbers([]);
            }, 300);
        } else {
            navigate("/search/vins", { state: { vin_numbers: vinString } });
            setVinNumbers([]);
        }
    };

    const removeSearchTag = (indexToRemove: number) => {
        const updatedVinNumbers = vinNumbers.filter(
            (_, index) => index !== indexToRemove
        );
        setVinNumbers(updatedVinNumbers);

        if (updatedVinNumbers.length <= 2) {
            vinPanelRef.current?.hide();
        }
    };

    const toggleVinPanel = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (vinNumbers.length === 0) {
            return;
        }
        vinPanelRef.current?.toggle(event);
    };

    const setSearchTag = (event: any) => {
        const value = event.target.value;
        setSearch(value);

        if (event.key === "Enter" && value.trim()) {
            event.preventDefault();
            const trimmedValue = value.trim();
            const vinsToAdd = trimmedValue
                .split(/[\s,]+/)
                .filter((item: string) => !isEmpty(item.trim()));

            vinsToAdd.forEach((vin: string) => {
                const trimmedVin = vin.trim();
                if (trimmedVin && !vinNumbers.includes(trimmedVin)) {
                    setVinNumbers((prev: string[]) => [...prev, trimmedVin]);
                }
            });

            setSearch("");
            if (inputSearchRef && inputSearchRef.current) {
                inputSearchRef.current.value = "";
            }
        }
    };

    const setSearchByPasteTag = (event: any) => {
        event.preventDefault();
        const vins: any = [...vinNumbers];
        const pastedText = event.clipboardData.getData("text/plain");
        const lines = pastedText.split("\n");
        const allVins: string[] = [];

        lines.forEach((line: string) => {
            const lineVins = line
                .split(/[\s,]+/)
                .filter((item: string) => !isEmpty(item.trim()));
            lineVins.forEach((vin: string) => {
                const trimmedVin = vin.trim();
                if (trimmedVin && !vins.includes(trimmedVin)) {
                    allVins.push(trimmedVin);
                }
            });
        });

        setVinNumbers([...vins, ...allVins]);

        setTimeout(() => {
            setSearch("");
            if (inputSearchRef && inputSearchRef.current) {
                inputSearchRef.current.value = "";
            }
        }, 100);
    };

    const removeAllSearchTag = () => {
        setVinNumbers([]);
        vinPanelRef.current?.hide();
        if (location.pathname === "/orders") {
            const event = new CustomEvent("searchEvent", {
                detail: { external_vin_numbers: [] },
            });

            window.dispatchEvent(event);
        }
    };

    const showCompactVins = vinNumbers.length > 2;

    return (
        <div className="layout-topbar app-topbar">
            <div className="app-topbar__left topbar-start">
                <button
                    ref={menubuttonRef}
                    type="button"
                    className="topbar-menubutton p-link p-trigger"
                    onClick={toggleSidebar}
                    aria-label={t("menu")}
                >
                    <i className="pi pi-bars" />
                </button>

                {showTrail ? (
                    <nav className="app-topbar__crumb" aria-label="Breadcrumb">
                        {pageSection ? (
                            <>
                                <span className="app-topbar__crumb-section">{pageSection}</span>
                                <i className="pi pi-angle-right app-topbar__crumb-sep" aria-hidden="true" />
                            </>
                        ) : null}
                        <span className="app-topbar__crumb-current">{pageTitle}</span>
                    </nav>
                ) : null}
            </div>

            <div className="app-topbar__right topbar-end">
                <div className="app-topbar__dock">
                    <ul className="topbar-menu">
                        {auth.hasAnyAccess(["ordersview"]) && (
                            <li className="topbar-search app-topbar__search">
                                <div className="app-search">
                                    <div className="d-inline-block" id="header-search-vin">
                                        <div className="position-relative custom-tag-search-container">
                                            <div className="custom-tag-container">
                                                <div className="position-relative">
                                                    <ul
                                                        className={`form-control custom-tag-multi-value-container surface-border border-2 border-round${
                                                            vinNumbers.length > 0 ? " has-vins" : ""
                                                        }${showCompactVins ? " is-compact" : ""}`}
                                                    >
                                                        {showCompactVins ? (
                                                            <li className="custom-tag-value-item app-topbar__vin-summary">
                                                                <button
                                                                    type="button"
                                                                    className="app-topbar__vin-summary-toggle"
                                                                    onClick={toggleVinPanel}
                                                                    aria-expanded={false}
                                                                    aria-haspopup="listbox"
                                                                >
                                                                    {vinNumbers.length} {t("vin(s)")}
                                                                    <i
                                                                        className="pi pi-chevron-down app-topbar__vin-summary-chevron"
                                                                        aria-hidden="true"
                                                                    />
                                                                </button>
                                                                <button
                                                                    className="close-indicator ms-1"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        removeAllSearchTag();
                                                                    }}
                                                                    type="button"
                                                                    aria-label="Clear"
                                                                >
                                                                    <i className="fas fa-times" />
                                                                </button>
                                                            </li>
                                                        ) : (
                                                            vinNumbers.map(
                                                                (vinNumber: string, index: number) => (
                                                                    <li
                                                                        key={index}
                                                                        className="custom-tag-value-item"
                                                                        title={vinNumber}
                                                                    >
                                                                        {vinNumber}
                                                                        <button
                                                                            className="close-indicator ms-1"
                                                                            onClick={() =>
                                                                                removeSearchTag(index)
                                                                            }
                                                                            type="button"
                                                                            aria-label={t("remove")}
                                                                        >
                                                                            <i className="fas fa-times" />
                                                                        </button>
                                                                    </li>
                                                                )
                                                            )
                                                        )}
                                                        <input
                                                            ref={inputSearchRef}
                                                            className="custom-tag-search-input"
                                                            placeholder={t("enter vin(s)")}
                                                            defaultValue={search}
                                                            onKeyUp={setSearchTag}
                                                            onPaste={setSearchByPasteTag}
                                                        />
                                                    </ul>
                                                    <OverlayPanel
                                                        ref={vinPanelRef}
                                                        className="app-topbar__vin-panel"
                                                        dismissable
                                                    >
                                                        <div className="app-topbar__vin-panel-header">
                                                            <span className="app-topbar__vin-panel-title">
                                                                {vinNumbers.length} {t("vin(s)")}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="app-topbar__vin-panel-clear"
                                                                onClick={removeAllSearchTag}
                                                            >
                                                                Clear all
                                                            </button>
                                                        </div>
                                                        <ul className="app-topbar__vin-panel-list">
                                                            {vinNumbers.map(
                                                                (vinNumber: string, index: number) => (
                                                                    <li
                                                                        key={`${vinNumber}-${index}`}
                                                                        className="app-topbar__vin-panel-item"
                                                                    >
                                                                        <span
                                                                            className="app-topbar__vin-panel-vin"
                                                                            title={vinNumber}
                                                                        >
                                                                            {vinNumber}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            className="app-topbar__vin-panel-remove"
                                                                            onClick={() =>
                                                                                removeSearchTag(index)
                                                                            }
                                                                            aria-label={t("remove")}
                                                                        >
                                                                            <i className="fas fa-times" />
                                                                        </button>
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    </OverlayPanel>
                                                    <button
                                                        className="btn-vin-search"
                                                        onClick={onSearchVin}
                                                        title="Search"
                                                        type="button"
                                                    >
                                                        <i className="fas fa-search" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )}

                        <li className="app-topbar__toolbar-actions">
                            <Button
                                type="button"
                                icon="pi pi-bell"
                                text
                                rounded
                                severity="secondary"
                                className="app-topbar__action-btn flex-shrink-0"
                                aria-label={t("notifications")}
                            />
                            <Button
                                type="button"
                                icon="pi pi-cog"
                                text
                                rounded
                                severity="secondary"
                                className="app-topbar__action-btn flex-shrink-0"
                                onClick={onConfigButtonClick}
                                aria-label={t("settings")}
                            />
                            <span className="app-topbar__profile topbar-profile">
                                <button type="button" className="p-link" onClick={showProfileSidebar}>
                                    {user && user.profile && user.profile.id && (
                                        <img
                                            className="border-circle"
                                            src={`${process.env.REACT_APP_DOCUMENT_URL}/${user.profile.id}`}
                                            alt={profileName}
                                        />
                                    )}

                                    {user && !user.profile && (
                                        <img
                                            className="border-circle"
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(profileName || "U")}&background=E06469&color=fff`}
                                            alt={profileName}
                                        />
                                    )}
                                </button>
                            </span>
                        </li>

                        <li className="ml-4 mr-4">
                            <div
                                className="loader"
                                style={{ visibility: "hidden" }}
                                id="preloader"
                            />
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
});

AppTopbar.displayName = "AppTopbar";

export default AppTopbar;
